import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useAuthorDashboard } from '../AuthorDashboardContext';
import { doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  ComposableMap, Geographies, Geography, Marker, ZoomableGroup
} from 'react-simple-maps';
import {
  Users, Globe, MapPin, Search, ArrowUpDown, Copy, Mail,
  CheckCircle, BookOpen, Flag, ThumbsUp, ThumbsDown, Trash2, AlertTriangle
} from 'lucide-react';

// ─────────────────────────────────────────────
// Country centroid lookup (lat/lng for map pins)
// ─────────────────────────────────────────────
const COUNTRY_CENTROIDS = {
  'Afghanistan': [33, 65], 'Albania': [41, 20], 'Algeria': [28, 3], 'Argentina': [-34, -64],
  'Australia': [-25, 134], 'Austria': [47.3, 13.3], 'Bangladesh': [24, 90],
  'Belgium': [50.8, 4.3], 'Brazil': [-10, -55], 'Bulgaria': [43, 25],
  'Canada': [56, -96], 'Chile': [-30, -71], 'China': [35, 105], 'Colombia': [4, -72],
  'Croatia': [45.2, 15.5], 'Czech Republic': [49.8, 15.5], 'Czechia': [49.8, 15.5],
  'Denmark': [56, 10], 'Ecuador': [-2, -77.5], 'Egypt': [27, 30],
  'Estonia': [59, 26], 'Ethiopia': [8, 38],
  'Finland': [64, 26], 'France': [46, 2], 'Germany': [51, 10],
  'Ghana': [8, -2], 'Greece': [39, 22], 'Hungary': [47, 20],
  'Iceland': [65, -18], 'India': [22, 78], 'Indonesia': [-2, 118],
  'Iran': [32, 53], 'Iraq': [33, 44], 'Ireland': [53.4, -7.9],
  'Israel': [31, 35], 'Italy': [42, 12.5], 'Jamaica': [18.1, -77.3],
  'Japan': [36, 138], 'Jordan': [31, 36], 'Kazakhstan': [48, 67],
  'Kenya': [1, 38], 'Latvia': [57, 25], 'Lithuania': [55.5, 24],
  'Luxembourg': [49.8, 6.1], 'Malaysia': [4, 109.5], 'Mexico': [23, -102],
  'Morocco': [32, -5], 'Netherlands': [52.5, 5.7], 'New Zealand': [-41, 175],
  'Nigeria': [10, 8], 'Norway': [62, 10],
  'Pakistan': [30, 70], 'Peru': [-10, -76], 'Philippines': [13, 122],
  'Poland': [52, 20], 'Portugal': [39.5, -8], 'Romania': [46, 25],
  'Russia': [62, 105], 'Saudi Arabia': [24, 45],
  'Serbia': [44, 21], 'Singapore': [1.35, 103.8], 'Slovakia': [48.7, 19.7],
  'Slovenia': [46.1, 14.8], 'South Africa': [-29, 24], 'South Korea': [36, 128],
  'Spain': [40, -4], 'Sri Lanka': [7.9, 80.8], 'Sweden': [63, 16],
  'Switzerland': [47, 8.2], 'Taiwan': [23.5, 121], 'Tanzania': [-6.4, 34.9],
  'Thailand': [15, 101], 'Trinidad And Tobago': [10.5, -61.3],
  'Turkey': [39, 35], 'Türkiye': [39, 35],
  'Uganda': [1.4, 32.3], 'Ukraine': [49, 32],
  'United Arab Emirates': [24, 54],
  'United Kingdom': [54, -2],
  'United States': [39, -98],
  'Uruguay': [-33, -56], 'Venezuela': [8, -66], 'Vietnam': [16, 108],
  'Zimbabwe': [-19.8, 29.9],
};

// Sorted list for the country dropdown
const COUNTRY_LIST = Object.keys(COUNTRY_CENTROIDS).sort();

function getCountryCentroid(country) {
  if (!country) return null;
  const normalised = country.trim().replace(/\b\w/g, c => c.toUpperCase());
  return COUNTRY_CENTROIDS[normalised] || null;
}

// ─────────────────────────────────────────────
// World map TopoJSON URL
// ─────────────────────────────────────────────
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
  { value: 'contacted', label: 'Contacted', color: 'bg-teal-100 text-teal-800' },
  { value: 'accepted', label: 'Accepted', color: 'bg-green-100 text-green-800' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-purple-100 text-purple-800' },
  { value: 'completed', label: 'Completed', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'declined', label: 'Declined', color: 'bg-red-100 text-red-700' },
];

const REVIEW_OPTIONS = [
  { value: 'all', label: 'All members', color: 'bg-green-100 text-green-800' },
  { value: 'most', label: 'Most members', color: 'bg-teal-100 text-teal-800' },
  { value: 'some', label: 'Some members', color: 'bg-slate-100 text-slate-700' },
  { value: 'cannot', label: 'Cannot commit', color: 'bg-red-100 text-red-700' },
];

const TOP_CHOICE_LABELS = {
  signedBooks: 'Signed books',
  art: 'Character art',
  gift: 'Gift pack',
  qa: 'Live Q&A',
};

function getStatusBadge(status) {
  const opt = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
  return <Badge className={`${opt.color} border-0`}>{opt.label}</Badge>;
}

function getReviewBadge(value) {
  const opt = REVIEW_OPTIONS.find(o => o.value === value) || REVIEW_OPTIONS[0];
  return <Badge className={`${opt.color} border-0`}>{opt.label}</Badge>;
}

function timeAgo(ts) {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// ─────────────────────────────────────────────
// Toast (lightweight, auto-dismiss)
// ─────────────────────────────────────────────
function Toast({ message, visible }) {
  if (!visible) return null;
  return (
    <div className="fixed bottom-6 left-1/2 z-[100] -translate-x-1/2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm text-white shadow-lg animate-in fade-in slide-in-from-bottom-2">
      {message}
    </div>
  );
}

// ─────────────────────────────────────────────
// World Map component
// ─────────────────────────────────────────────
function BookClubMap({ clubs }) {
  const countryData = useMemo(() => {
    const map = {};
    clubs.forEach(club => {
      const country = club.locationCountry;
      if (!country) return; // skip unmapped
      if (!map[country]) map[country] = { country, participants: 0, clubs: 0 };
      map[country].participants += club.participants || 0;
      map[country].clubs += 1;
    });
    return Object.values(map).map(d => ({
      ...d,
      coords: getCountryCentroid(d.country),
    })).filter(d => d.coords);
  }, [clubs]);

  const unmapped = useMemo(() =>
    clubs.filter(c => !c.locationCountry || !getCountryCentroid(c.locationCountry)),
    [clubs]
  );

  const maxParticipants = Math.max(...countryData.map(d => d.participants), 1);

  if (clubs.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-6 text-center">
          <Globe className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm text-slate-500">Clubs will appear on the map once applications come in.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 overflow-hidden">
      <CardContent className="p-0">
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{ scale: 130, center: [0, 30] }}
          className="w-full"
          style={{ maxHeight: 400 }}
        >
          <ZoomableGroup>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map(geo => (
                  <Geography
                    key={geo.rpiKey || geo.id}
                    geography={geo}
                    fill="#e2e8f0"
                    stroke="#cbd5e1"
                    strokeWidth={0.5}
                    style={{
                      hover: { fill: '#cbd5e1' },
                      pressed: { fill: '#94a3b8' },
                    }}
                  />
                ))
              }
            </Geographies>
            {countryData.map(d => {
              const radius = 4 + (Math.sqrt(d.participants) / Math.sqrt(maxParticipants)) * 10;
              return (
                <Marker key={d.country} coordinates={[d.coords[1], d.coords[0]]}>
                  <circle
                    r={radius}
                    fill="hsl(174, 58%, 39%)"
                    fillOpacity={0.7}
                    stroke="hsl(174, 58%, 30%)"
                    strokeWidth={1}
                  />
                  <title>{`${d.country}: ${d.clubs} club${d.clubs !== 1 ? 's' : ''}, ${d.participants} readers`}</title>
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>
        {/* Text fallback list below map */}
        <div className="border-t border-slate-100 px-4 py-3">
          <div className="flex flex-wrap gap-3 text-xs text-slate-500">
            {countryData.sort((a, b) => b.participants - a.participants).map(d => (
              <span key={d.country} className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full bg-teal-500" />
                {d.country}: {d.participants} readers ({d.clubs} club{d.clubs !== 1 ? 's' : ''})
              </span>
            ))}
          </div>
        </div>
        {/* Unmapped locations badge */}
        {unmapped.length > 0 && (
          <div className="border-t border-slate-100 px-4 py-2.5 bg-slate-50">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge className="bg-orange-100 text-orange-700 border-0">
                Unmapped locations ({unmapped.length})
              </Badge>
              {unmapped.map(c => (
                <span key={c.id} className="text-slate-500">
                  {c.location} ({c.contact?.name || 'unknown'})
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────
// Confirmation dialog
// ─────────────────────────────────────────────
function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel, destructive }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className={`h-5 w-5 ${destructive ? 'text-red-500' : 'text-slate-500'}`} />
            {title}
          </DialogTitle>
        </DialogHeader>
        <p className="mt-2 text-sm text-slate-600">{message}</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            className={destructive ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// Club detail modal
// ─────────────────────────────────────────────
function ClubDetailModal({ club, open, onClose, basePath, onToast }) {
  const [status, setStatus] = useState(club?.status || 'new');
  const [notes, setNotes] = useState(club?.notes || '');
  const [locationCountry, setLocationCountry] = useState(club?.locationCountry || '');
  const [countryMode, setCountryMode] = useState('select'); // 'select' or 'custom'
  const [customCountry, setCustomCountry] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [confirmReject, setConfirmReject] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (club) {
      setStatus(club.status || 'new');
      setNotes(club.notes || '');
      const country = club.locationCountry || '';
      setLocationCountry(country);
      if (country && !COUNTRY_LIST.includes(country)) {
        setCountryMode('custom');
        setCustomCountry(country);
      } else {
        setCountryMode('select');
        setCustomCountry('');
      }
    }
  }, [club]);

  const saveField = useCallback(async (field, value) => {
    if (!club || !basePath) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, basePath, 'bookClubs', club.id), {
        [field]: value,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Failed to save:', err.message);
    }
    setSaving(false);
  }, [club, basePath]);

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setStatus(newStatus);
    saveField('status', newStatus);
  };

  const handleNotesChange = (e) => {
    const newNotes = e.target.value;
    setNotes(newNotes);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveField('notes', newNotes), 800);
  };

  const handleCountryChange = (e) => {
    const val = e.target.value;
    if (val === '__custom__') {
      setCountryMode('custom');
      setCustomCountry('');
    } else {
      setCountryMode('select');
      setLocationCountry(val);
      saveField('locationCountry', val || null);
    }
  };

  const handleCustomCountrySave = () => {
    const val = customCountry.trim();
    setLocationCountry(val || null);
    saveField('locationCountry', val || null);
    onToast(val ? `Country set to "${val}"` : 'Country cleared');
  };

  const handleAccept = async () => {
    setStatus('accepted');
    await saveField('status', 'accepted');
    onToast(`${club.contact?.name || 'Application'} accepted`);
    onClose();
  };

  const handleReject = async () => {
    setConfirmReject(false);
    setStatus('declined');
    await saveField('status', 'declined');
    onToast(`${club.contact?.name || 'Application'} declined`);
    onClose();
  };

  const handleDelete = async () => {
    if (!club || !basePath) return;
    setConfirmDelete(false);
    try {
      await deleteDoc(doc(db, basePath, 'bookClubs', club.id));
      onToast('Application deleted');
      onClose();
    } catch (err) {
      console.error('Failed to delete:', err.message);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    } catch { /* fallback — user can select manually */ }
  };

  if (!club) return null;

  const ranking = club.ranking || {};
  const rankItems = [
    { key: 'signedBooks', label: 'Signed books', rank: ranking.signedBooks },
    { key: 'art', label: 'Character art print', rank: ranking.art },
    { key: 'gift', label: 'Gift pack', rank: ranking.gift },
    { key: 'qa', label: 'Live Q&A session', rank: ranking.qa },
  ].sort((a, b) => (a.rank || 99) - (b.rank || 99));

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-teal-600" />
              {club.location}
            </DialogTitle>
          </DialogHeader>

          {/* Accept / Reject buttons */}
          {club.status !== 'accepted' && club.status !== 'declined' && (
            <div className="mt-3 flex gap-2">
              <Button
                size="sm"
                className="flex-1 gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                onClick={handleAccept}
              >
                <ThumbsUp className="h-4 w-4" /> Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => setConfirmReject(true)}
              >
                <ThumbsDown className="h-4 w-4" /> Reject
              </Button>
            </div>
          )}
          {(club.status === 'accepted' || club.status === 'declined') && (
            <div className="mt-3">
              {getStatusBadge(club.status)}
            </div>
          )}

          <div className="mt-4 space-y-5">
            {/* Club info */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-500">Participants</span>
                <p className="font-medium">{club.participants}</p>
              </div>
              <div>
                <span className="text-slate-500">Country</span>
                {countryMode === 'select' ? (
                  <Select
                    value={locationCountry || ''}
                    onChange={handleCountryChange}
                    className="mt-0.5 h-8 text-sm"
                  >
                    <option value="">— Not set —</option>
                    {COUNTRY_LIST.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                    <option value="__custom__">Other (type manually)</option>
                  </Select>
                ) : (
                  <div className="flex gap-1 mt-0.5">
                    <Input
                      value={customCountry}
                      onChange={(e) => setCustomCountry(e.target.value)}
                      placeholder="Type country..."
                      className="h-8 text-sm"
                    />
                    <Button size="sm" className="h-8 px-2 text-xs" onClick={handleCustomCountrySave}>
                      Save
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => { setCountryMode('select'); }}>
                      List
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <span className="text-slate-500">Will review</span>
                <p className="mt-0.5">{getReviewBadge(club.willReview)}</p>
              </div>
              <div>
                <span className="text-slate-500">Submitted</span>
                <p className="font-medium">{timeAgo(club.submittedAt || club.createdAt)}</p>
              </div>
            </div>

            {/* Ranking */}
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Reward Ranking</h4>
              <div className="space-y-1">
                {rankItems.map((item, i) => (
                  <div key={item.key} className="flex items-center gap-2 text-sm">
                    <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                      i === 0 ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {item.rank || '—'}
                    </span>
                    <span className={i === 0 ? 'font-medium text-teal-700' : 'text-slate-600'}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* About */}
            {club.about && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-1">About the club</h4>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{club.about}</p>
              </div>
            )}

            {/* Contact */}
            <div className="rounded-lg border border-slate-200 p-3">
              <h4 className="text-sm font-medium text-slate-700 mb-2">Contact</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">{club.contact?.name}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-600 truncate">{club.contact?.email}</span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => copyToClipboard(club.contact?.email, 'email')}
                    >
                      {copied === 'email' ? <CheckCircle className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                      <span className="ml-1">{copied === 'email' ? 'Copied' : 'Copy'}</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => { window.location.href = `mailto:${club.contact?.email}`; }}
                    >
                      <Mail className="h-3 w-3" />
                      <span className="ml-1">Email</span>
                    </Button>
                  </div>
                </div>
                {club.contact?.address && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-600 truncate">{club.contact.address}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs flex-shrink-0"
                      onClick={() => copyToClipboard(club.contact.address, 'address')}
                    >
                      {copied === 'address' ? <CheckCircle className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                      <span className="ml-1">{copied === 'address' ? 'Copied' : 'Copy'}</span>
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Status & Notes */}
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Status</label>
                <Select
                  value={status}
                  onChange={handleStatusChange}
                  className="mt-1"
                >
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Notes</label>
                <Textarea
                  value={notes}
                  onChange={handleNotesChange}
                  placeholder="Add your notes about this club..."
                  className="mt-1"
                  rows={3}
                />
              </div>
              {saving && <p className="text-xs text-teal-600">Saving...</p>}
            </div>

            {/* Delete — visually separated */}
            <div className="border-t border-slate-200 pt-4">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete application
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject confirmation */}
      <ConfirmDialog
        open={confirmReject}
        onClose={() => setConfirmReject(false)}
        onConfirm={handleReject}
        title="Reject application?"
        message={`This will set the status to Declined for ${club.contact?.name || 'this application'}.`}
        confirmLabel="Reject"
        destructive
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete application?"
        message={`This will permanently delete the application from ${club.contact?.name || 'unknown'}. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
      />
    </>
  );
}

// ─────────────────────────────────────────────
// Main tab component
// ─────────────────────────────────────────────
export default function AuthorBookClubsTab() {
  const { bookClubs, basePath } = useAuthorDashboard();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('submittedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedClub, setSelectedClub] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Keep selectedClub in sync with live data (so edits reflect without re-opening)
  const liveSelectedClub = useMemo(() => {
    if (!selectedClub) return null;
    return bookClubs.find(c => c.id === selectedClub.id) || null;
  }, [selectedClub, bookClubs]);

  // ── Stats ──
  const stats = useMemo(() => {
    const total = bookClubs.length;
    const totalReaders = bookClubs.reduce((sum, c) => sum + (c.participants || 0), 0);
    const countries = new Set(bookClubs.map(c => c.locationCountry).filter(Boolean));
    const scotlandUk = bookClubs.filter(c => {
      const country = (c.locationCountry || '').toLowerCase();
      return country === 'united kingdom';
    }).length;
    return { total, totalReaders, countries: countries.size, scotlandUk };
  }, [bookClubs]);

  // ── Filtering & sorting ──
  const filteredClubs = useMemo(() => {
    let list = [...bookClubs];

    if (statusFilter !== 'all') {
      list = list.filter(c => c.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        (c.location || '').toLowerCase().includes(q) ||
        (c.contact?.name || '').toLowerCase().includes(q) ||
        (c.locationCountry || '').toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case 'location': aVal = a.location || ''; bVal = b.location || ''; break;
        case 'participants': aVal = a.participants || 0; bVal = b.participants || 0; break;
        case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
        case 'submittedAt':
        default:
          aVal = a.submittedAt?.toMillis?.() || a.submittedAt?.seconds * 1000 || 0;
          bVal = b.submittedAt?.toMillis?.() || b.submittedAt?.seconds * 1000 || 0;
          break;
      }
      if (typeof aVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return list;
  }, [bookClubs, statusFilter, search, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Book Clubs</h1>
        <p className="text-sm text-slate-500">Applications from book clubs interested in your work.</p>
      </div>

      {/* Dashboard strip */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100">
                <BookOpen className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                <p className="text-xs text-slate-500">Total clubs</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100">
                <Users className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.totalReaders}</p>
                <p className="text-xs text-slate-500">Total readers</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100">
                <Globe className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.countries}</p>
                <p className="text-xs text-slate-500">Countries reached</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100">
                <Flag className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.scotlandUk}</p>
                <p className="text-xs text-slate-500">Scotland / UK (in-person)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* World map */}
      <BookClubMap clubs={bookClubs} />

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by location or contact name..."
            className="pl-9"
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full sm:w-44"
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </Select>
      </div>

      {/* Table */}
      {filteredClubs.length === 0 ? (
        <Card className="border-slate-200 border-dashed">
          <CardContent className="p-8 text-center">
            <Users className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">
              {bookClubs.length === 0
                ? 'No book club applications yet. They\'ll appear here once clubs start applying.'
                : 'No clubs match your current filters.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-left">
                  <th className="px-4 py-3 font-medium text-slate-600">
                    <button onClick={() => toggleSort('location')} className="flex items-center gap-1 hover:text-slate-900">
                      Location <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600">
                    <button onClick={() => toggleSort('participants')} className="flex items-center gap-1 hover:text-slate-900">
                      Readers <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600 hidden sm:table-cell">Top choice</th>
                  <th className="px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Will review</th>
                  <th className="px-4 py-3 font-medium text-slate-600">
                    <button onClick={() => toggleSort('status')} className="flex items-center gap-1 hover:text-slate-900">
                      Status <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">
                    <button onClick={() => toggleSort('submittedAt')} className="flex items-center gap-1 hover:text-slate-900">
                      Submitted <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredClubs.map(club => (
                  <tr
                    key={club.id}
                    onClick={() => setSelectedClub(club)}
                    className="border-b border-slate-50 cursor-pointer transition-colors hover:bg-slate-50"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') setSelectedClub(club); }}
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900">{club.location}</p>
                        <p className="text-xs text-slate-400">{club.contact?.name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium">{club.participants}</td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-slate-600">{TOP_CHOICE_LABELS[club.topChoice] || '—'}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">{getReviewBadge(club.willReview)}</td>
                    <td className="px-4 py-3">{getStatusBadge(club.status)}</td>
                    <td className="px-4 py-3 text-slate-500 hidden lg:table-cell">
                      {timeAgo(club.submittedAt || club.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Detail modal */}
      <ClubDetailModal
        club={liveSelectedClub}
        open={!!liveSelectedClub}
        onClose={() => setSelectedClub(null)}
        basePath={basePath}
        onToast={showToast}
      />

      <Toast message={toast} visible={!!toast} />
    </div>
  );
}
